import { EnvelopeIcon, MapPinIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { fullSingleLineAddress, HomeCareLeadId } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { EmailComposeTrigger } from "components/contact-display/email-display";
import { PhoneActions } from "components/contact-display/phone-display";
import { trpc } from "components/rpc/trpc";
import {
    RoundActionExternalLink,
    RoundActionTrigger,
} from "components/ui/round-action-button";
import { Spinner } from "components/ui/spinner";
import { AdvancedSearchEntityType } from "generated/graphql";
import { computeDirectionsUrl } from "helpers/address";
import React from "react";
import { mailtoUri } from "util/uri";
import { preferredEmail, preferredPhone } from "./contact-channel";

interface ClientMapContactActionsProps {
    leadId: HomeCareLeadId;
}

export const ClientMapContactActions: React.FC<ClientMapContactActionsProps> = props => {
    const { leadId } = props;
    const trpcClient = trpc.useTRPC();
    const query = useQuery(trpcClient.homeCareLead.get.queryOptions({ id: leadId }));

    const contactsLoaded = !!query.data;
    const bestContactEntity = contactsLoaded ? query.data.bestContactEntity : null;
    const selfContactEntity = contactsLoaded ? query.data.selfContactEntity : null;
    const phone = preferredPhone(bestContactEntity, selfContactEntity);
    const email = preferredEmail(bestContactEntity, selfContactEntity);
    const address = selfContactEntity ? fullSingleLineAddress(selfContactEntity) : null;

    if (query.isLoading) {
        return <Spinner />;
    }

    return (
        <div className="flex items-center gap-1.5">
            {phone ? (
                <PhoneActions
                    value={phone}
                    intent="chooser"
                    entityId={leadId}
                    entityType={AdvancedSearchEntityType.HomeCareLead}
                    renderTrigger={binding => (
                        <RoundActionTrigger
                            title={binding.title}
                            Icon={PhoneIcon}
                            href={binding.href}
                            onClick={binding.onClick}
                        />
                    )}
                />
            ) : null}
            {email ? (
                <EmailComposeTrigger
                    value={email.address}
                    entityId={leadId}
                    entityType={AdvancedSearchEntityType.HomeCareLead}
                    renderTrigger={open => (
                        <RoundActionTrigger
                            title={`Email ${email.address}`}
                            Icon={EnvelopeIcon}
                            href={mailtoUri(email.address)}
                            onClick={open}
                        />
                    )}
                />
            ) : null}
            {address ? (
                <RoundActionExternalLink
                    title="Get directions"
                    Icon={MapPinIcon}
                    href={computeDirectionsUrl(address, "")}
                />
            ) : null}
        </div>
    );
};
